import { Alert, Box, Dialog, DialogActions, DialogContent, DialogTitle, Grid2, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ActualAuctionStatusEnum } from "../../../_common/enum/actualAuctionStatus";
import { AuctionStatusEnum } from "../../../_common/enum/auctionStatus.enum";
import { IDropdownData } from "../../../_common/interface/dropdown/iDropdownData.interface";
import { StandardButton } from "../../../_components/button/primary.button";
import { accountService } from "../../../_services/accounts.service";
import { auctionHeaderService } from "../../../_services/auctionHeader.service";
import { useAuctionUser } from "../../../_hubs/auctionUser.hub";
import { handleErrorResponse } from "../../../_helpers/handleResponse.helper";
import { auctionService } from "../../../_services/auctionService.service";
import { catalogueService } from "../../../_services/catalogueService.service";
import { CatalogueItemOnlineCard } from "../components/auction-catalogueItemOnlineCard";
import { CatalogueItemStatusCard } from "../components/auction-catalogueItemStatusCard";
import { AuctionMessages } from "../components/auction-messages";
import { AuctionAnnualCustomerBasketPanel } from "./auction-annual-customer-basket-panel";
import { AuctionAnnualCustomerTurnOrder } from "./auction-annual-customer-turn-order";
import { AuctionAnnualCataloguePanel } from "./auction-annual-catalogue-panel";
import { AuctionAnnualCountdown } from "./auction-annual-countdown";
import { useAnnualTurnStartAlarm } from "./auction-annual-turn-alarm";
import {
    AnnualBauRestrictionRow,
    AuctionAnnualStateResponse,
    AuctionAnnualTurnResponse,
    BasketHistoryEntry,
    buildAnnualCatalogueItemMap,
    getAnnualActualStatus,
    reduceAnnualTurnHistory,
    sortAnnualCatalogueItems,
} from "./auction-annual-shared";

type TurnActionType = "accept" | "skip";

export function AuctionAnnualCustomerView(params: { auction?: any }) {
    const { connected, presence } = useAuctionUser();
    const [loading, setLoading] = useState({ catalogue: true, users: true });
    const [catalogues, setCatalogues] = useState<any[]>([]);
    const [activeCatalogueId, setActiveCatalogueId] = useState<number | null>(null);
    const [roundNumber, setRoundNumber] = useState(1);
    const [annualRoundLimit, setAnnualRoundLimit] = useState(12);
    const [lastRoundExtraAllowance, setLastRoundExtraAllowance] = useState(500);
    const [annualState, setAnnualState] = useState<AuctionAnnualStateResponse | null>(null);
    const [carryOverByUserId, setCarryOverByUserId] = useState<Record<number, number>>({});
    const [basketByUserId, setBasketByUserId] = useState<Record<number, number[]>>({});
    const [basketHistoryByUserId, setBasketHistoryByUserId] = useState<Record<number, BasketHistoryEntry[]>>({});
    const [pendingTurnItemIds, setPendingTurnItemIds] = useState<number[]>([]);
    const [annualTimerStatus, setAnnualTimerStatus] = useState(getAnnualActualStatus(AuctionStatusEnum.Pending));
    const [turnRequestLoading, setTurnRequestLoading] = useState(false);
    const [turnActionConfirm, setTurnActionConfirm] = useState<TurnActionType | null>(null);
    // True while a post-status-change refetch is in flight. The auction status arrives from the
    // realtime stream before the fresh turn state, so without this guard the client could briefly
    // think it is still their turn (e.g. Skip enabled for a second right after the admin advances).
    const [turnSyncPending, setTurnSyncPending] = useState(false);

    // Proxy bidders act on behalf of a chosen customer; a plain customer always bids as themselves.
    const canActOnBehalf = accountService.isProxyBidder;
    const [onBehalfUser, setOnBehalfUser] = useState<IDropdownData | null>(null);
    const onBehalfId = canActOnBehalf ? Number((onBehalfUser as any)?.id ?? 0) : 0;
    const onBehalfIdRef = useRef<number>(0);
    onBehalfIdRef.current = onBehalfId > 0 ? onBehalfId : 0;

    // Surface the proxy "on behalf of" picker in the global toolbar rather than on the page.
    useEffect(() => {
        if (!canActOnBehalf) {
            auctionHeaderService.set({ onBehalf: null });
            return;
        }
        auctionHeaderService.set({
            onBehalf: {
                catalogueId: activeCatalogueId ?? undefined,
                selected: onBehalfUser,
                onSelect: setOnBehalfUser,
            },
        });
    }, [canActOnBehalf, activeCatalogueId, onBehalfUser]);

    useEffect(() => () => auctionHeaderService.set({ onBehalf: null }), []);

    function applyAnnualStateFromServer(state: AuctionAnnualStateResponse | null) {
        if (!state) return;
        setAnnualState(state);
        setRoundNumber(Number(state.currentRoundNumber ?? 1) > 0 ? Number(state.currentRoundNumber) : 1);
        setAnnualRoundLimit(Math.min(12, Math.max(1, Number(state.maxRounds ?? 12))));
        setLastRoundExtraAllowance(Math.max(0, Number(state.lastRoundExtraAllowance ?? 500)));
    }

    function applyAnnualTurnHistoryFromServer(turns: AuctionAnnualTurnResponse[], clearPendingSelection = true) {
        const historyState = reduceAnnualTurnHistory(turns);
        setBasketByUserId(historyState.basketByUserId);
        setBasketHistoryByUserId(historyState.basketHistoryByUserId);
        setCarryOverByUserId(historyState.carryOverByUserId);
        if (clearPendingSelection) {
            setPendingTurnItemIds([]);
        }
    }

    async function refreshAnnualRealtime(auctionId: number) {
        setTurnSyncPending(true);
        try {
            const latestCatalogues = await catalogueService.getAllAuction(auctionId).catch(() => []);
            const safeCatalogues = Array.isArray(latestCatalogues) ? latestCatalogues : [];
            if (safeCatalogues.length > 0) {
                setCatalogues(safeCatalogues);
                const activeId = Number((safeCatalogues.find((c: any) => c?.isActive) ?? safeCatalogues[0])?.id ?? 0);
                if (activeId > 0) setActiveCatalogueId(activeId);
            }

            const annualStateResponse = await auctionService.getAnnualState(auctionId, onBehalfIdRef.current || undefined).catch(() => null);
            const annualTurns = await auctionService.getAnnualTurnHistory(auctionId, onBehalfIdRef.current || undefined).catch(() => []);
            const previousRound = Number(annualState?.currentRoundNumber ?? roundNumber);
            const previousTurnUser = Number(annualState?.currentCatalogueUserId ?? 0);
            const nextRound = Number(annualStateResponse?.currentRoundNumber ?? previousRound);
            const nextTurnUser = Number(annualStateResponse?.currentCatalogueUserId ?? previousTurnUser);
            const turnChanged = previousRound !== nextRound || previousTurnUser !== nextTurnUser;

            applyAnnualTurnHistoryFromServer(annualTurns, false);
            applyAnnualStateFromServer(annualStateResponse);
            const myId = Number(annualStateResponse?.myCatalogueUserId ?? 0);
            const isMyTurnAfterRefresh = myId > 0 && myId === nextTurnUser;
            if (turnChanged || !isMyTurnAfterRefresh) {
                setPendingTurnItemIds([]);
                setTurnActionConfirm(null);
            }
        } catch (err) {
            handleErrorResponse(err);
        } finally {
            setTurnSyncPending(false);
        }
    }

    useEffect(() => {
        if (!(params.auction?.id > 0)) {
            setCatalogues([]);
            setActiveCatalogueId(null);
            setRoundNumber(1);
            setAnnualRoundLimit(12);
            setLastRoundExtraAllowance(500);
            setAnnualState(null);
            setCarryOverByUserId({});
            setBasketByUserId({});
            setBasketHistoryByUserId({});
            setPendingTurnItemIds([]);
            setTurnActionConfirm(null);
            setLoading({ catalogue: false, users: false });
            return;
        }

        loadCustomerData(params.auction.id);
    // loadCustomerData is intentionally not a dependency to avoid reload loops on function identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.auction?.id]);

    useEffect(() => {
        setAnnualTimerStatus(getAnnualActualStatus(Number(params.auction?.auctionStatusId ?? 0)));
        if (params.auction?.id > 0) {
            refreshAnnualRealtime(params.auction.id);
        }
        // refreshAnnualRealtime intentionally omitted from deps; trigger only on status stream changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params.auction?.auctionStatusId]);

    useEffect(() => {
        if (!canActOnBehalf) return;
        if (!(params.auction?.id > 0)) return;
        (async () => {
            const stateResponse = await auctionService.getAnnualState(params.auction.id, onBehalfId || undefined).catch(() => null);
            const turns = await auctionService.getAnnualTurnHistory(params.auction.id, onBehalfId || undefined).catch(() => []);
            applyAnnualStateFromServer(stateResponse);
            applyAnnualTurnHistoryFromServer(turns);
            setPendingTurnItemIds([]);
            setTurnActionConfirm(null);
        })();
        // Reload the represented customer's state/history whenever the proxy switches user.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onBehalfId]);

    async function loadCustomerData(auctionId: number) {
        setLoading({ catalogue: true, users: true });
        try {
            const loadedCatalogues = await catalogueService.getAllAuction(auctionId);
            const allCatalogues = Array.isArray(loadedCatalogues) ? loadedCatalogues : [];
            setCatalogues(allCatalogues);

            const selectedCatalogue =
                allCatalogues.find((c: any) => c?.isActive) ??
                allCatalogues[0] ??
                null;
            const selectedCatalogueId = Number(selectedCatalogue?.id ?? 0);
            setActiveCatalogueId(selectedCatalogueId > 0 ? selectedCatalogueId : null);
            setLoading((prev) => ({ ...prev, catalogue: false }));

            const annualStateResponse = await auctionService.getAnnualState(auctionId, onBehalfIdRef.current || undefined).catch(() => null);
            const annualTurns = await auctionService.getAnnualTurnHistory(auctionId, onBehalfIdRef.current || undefined).catch(() => []);
            applyAnnualStateFromServer(annualStateResponse);
            applyAnnualTurnHistoryFromServer(annualTurns);
            setTurnActionConfirm(null);
            setLoading((prev) => ({ ...prev, users: false }));
        } catch (err) {
            handleErrorResponse(err);
            setLoading({ catalogue: false, users: false });
        }
    }

    const activeCatalogue = useMemo(
        () => catalogues.find((c: any) => Number(c?.id) === Number(activeCatalogueId)),
        [catalogues, activeCatalogueId],
    );
    const catalogueItems = useMemo(
        () => sortAnnualCatalogueItems(activeCatalogue?.catalogueItems),
        [activeCatalogue],
    );
    const catalogueItemsById = useMemo(() => {
        return buildAnnualCatalogueItemMap(catalogueItems);
    }, [catalogueItems]);

    function normalizeBauCode(code: string | null | undefined) {
        return String(code ?? "").trim().toUpperCase();
    }

    function getMyBauLimitMap() {
        const map: Record<string, number> = {};
        (annualState?.myBauRestrictions ?? []).forEach((row: AnnualBauRestrictionRow) => {
            const code = normalizeBauCode(row?.bauCode);
            const qty = Number(row?.quantity ?? 0);
            if (!code || !(qty > 0)) return;
            map[code] = qty;
        });
        return map;
    }

    const countBauUsage = useCallback((itemIds: number[]) => {
        const map: Record<string, number> = {};
        itemIds.forEach((itemId) => {
            const row = catalogueItemsById.get(itemId);
            const code = normalizeBauCode(row?.forestCode);
            if (!code) return;
            const volume = Number(row?.totalVolume ?? 0);
            if (!(volume > 0)) return;
            map[code] = (map[code] ?? 0) + volume;
        });
        return map;
    }, [catalogueItemsById]);

    const myCatalogueUserId = Number(annualState?.myCatalogueUserId ?? 0);
    const isRegistered = annualState?.isRegistered === true && myCatalogueUserId > 0;
    const currentTurnUserId = Number(annualState?.currentCatalogueUserId ?? 0);
    const isCurrentClientTurn = isRegistered && currentTurnUserId === myCatalogueUserId;

    const committedItemIds = useMemo(
        () => Object.values(basketByUserId).flat(),
        [basketByUserId],
    );

    // Server sends every committed lot id (no who-took-it). Merge with own basket/pending
    // so the left list disables taken lots for all customers.
    const occupiedItemIds = useMemo(() => {
        const fromServer = Array.isArray(annualState?.occupiedCatalogueItemIds)
            ? annualState!.occupiedCatalogueItemIds!.map((id) => Number(id)).filter((id) => id > 0)
            : [];
        return new Set<number>([...fromServer, ...committedItemIds, ...pendingTurnItemIds]);
    }, [annualState?.occupiedCatalogueItemIds, committedItemIds, pendingTurnItemIds]);

    const pendingTurnVolume = useMemo(
        () =>
            pendingTurnItemIds.reduce((total, itemId) => {
                const volume = Number(catalogueItemsById.get(itemId)?.totalVolume ?? 0);
                return total + (Number.isFinite(volume) ? volume : 0);
            }, 0),
        [pendingTurnItemIds, catalogueItemsById],
    );

    const myCommittedBauUsage = useMemo(
        () => countBauUsage(myCatalogueUserId > 0 ? (basketByUserId[myCatalogueUserId] ?? []) : []),
        [myCatalogueUserId, basketByUserId, countBauUsage],
    );
    const myPendingBauUsage = useMemo(
        () => countBauUsage(pendingTurnItemIds),
        [pendingTurnItemIds, countBauUsage],
    );

    const currentUserBaseVolume = Number(annualState?.myVolumePerRound ?? 0);
    const currentUserCarryOver = Number(myCatalogueUserId > 0 ? carryOverByUserId[myCatalogueUserId] ?? 0 : 0);
    const isLastRound = roundNumber >= annualRoundLimit;
    const currentUserAvailableVolume = Math.max(0, currentUserBaseVolume + currentUserCarryOver + (isLastRound ? lastRoundExtraAllowance : 0));
    const currentUserRemainingVolume = Math.max(0, currentUserAvailableVolume - pendingTurnVolume);

    // After this customer's last turn (or auction complete), do not project another round's
    // base/last-round bonus into Left — that made leftover look like 20 + 3000 + 500.
    const isAuctionCompleted =
        annualTimerStatus === ActualAuctionStatusEnum.Completed
        || Number(params.auction?.auctionStatusId ?? 0) === AuctionStatusEnum.Completed;
    const hasCompletedTurnThisRound = (basketHistoryByUserId[myCatalogueUserId] ?? []).some(
        (entry) => Number(entry.roundNumber) === Number(roundNumber),
    );
    const hasNoUpcomingAllowance =
        isAuctionCompleted
        || (isLastRound && hasCompletedTurnThisRound && !isCurrentClientTurn);
    const displayAvailableVolume = hasNoUpcomingAllowance ? 0 : currentUserAvailableVolume;
    const displaySelectedVolume = hasNoUpcomingAllowance ? 0 : pendingTurnVolume;
    const displayRemainingVolume = hasNoUpcomingAllowance ? 0 : currentUserRemainingVolume;

    const myBauLimitMap = getMyBauLimitMap();
    const myBauLeftMap: Record<string, number> = Object.keys(myBauLimitMap).reduce((acc, code) => {
        const limit = myBauLimitMap[code] ?? 0;
        const used = (myCommittedBauUsage[code] ?? 0) + (myPendingBauUsage[code] ?? 0);
        acc[code] = Math.max(0, limit - used);
        return acc;
    }, {} as Record<string, number>);
    const myBauLeftEntries = Object.keys(myBauLimitMap)
        .sort()
        .map((code) => {
            const limit = myBauLimitMap[code] ?? 0;
            const left = myBauLeftMap[code] ?? 0;
            const ratio = limit > 0 ? left / limit : 1;
            return {
                code,
                left,
                isLow: ratio < 0.2,
            };
        });
    const isPendingForTurnAdvance = annualTimerStatus === getAnnualActualStatus(AuctionStatusEnum.Pending);
    const effectiveAnnualStatus = connected ? annualTimerStatus : ActualAuctionStatusEnum.UserDisconnected;
    const canClientAct = connected && isRegistered && isCurrentClientTurn && !isPendingForTurnAdvance && !turnSyncPending && annualTimerStatus === ActualAuctionStatusEnum.Active;
    const hasPendingSelection = pendingTurnItemIds.length > 0;
    const { isPlaying: isTurnAlarmPlaying, stopAlarm: stopTurnAlarm } = useAnnualTurnStartAlarm({
        myCatalogueUserId,
        currentTurnUserId,
        annualTimerStatus,
        connected,
        isRegistered,
        turnSyncPending,
    });

    function togglePendingItem(itemId: number) {
        if (!canClientAct) return;

        const isAlreadySelected = pendingTurnItemIds.includes(itemId);
        if (isAlreadySelected) {
            setPendingTurnItemIds((prev) => prev.filter((id) => id !== itemId));
            return;
        }

        if (committedItemIds.includes(itemId)) return;
        if (occupiedItemIds.has(itemId)) return;

        const itemVolume = Number(catalogueItemsById.get(itemId)?.totalVolume ?? 0);
        if (itemVolume <= 0) return;
        if (pendingTurnVolume + itemVolume > currentUserAvailableVolume) return;

        const bauCode = normalizeBauCode(catalogueItemsById.get(itemId)?.forestCode);
        const bauLimit = myBauLimitMap[bauCode];
        if (bauCode && bauLimit > 0) {
            const used = (myCommittedBauUsage[bauCode] ?? 0) + (myPendingBauUsage[bauCode] ?? 0);
            if ((used + itemVolume) > bauLimit) return;
        }

        setPendingTurnItemIds((prev) => [...prev, itemId]);
    }

    async function completeTurn(skipOnly: boolean) {
        if (!params.auction?.id || !canClientAct || !(myCatalogueUserId > 0)) return;

        setTurnRequestLoading(true);
        try {
            const selectedCatalogueItemIds = skipOnly ? [] : pendingTurnItemIds;
            await auctionService.completeAnnualTurn(params.auction.id, myCatalogueUserId, selectedCatalogueItemIds);
            const annualStateResponse = await auctionService.getAnnualState(params.auction.id, onBehalfIdRef.current || undefined).catch(() => null);
            const annualTurns = await auctionService.getAnnualTurnHistory(params.auction.id, onBehalfIdRef.current || undefined).catch(() => []);
            applyAnnualStateFromServer(annualStateResponse);
            applyAnnualTurnHistoryFromServer(annualTurns);
            setTurnActionConfirm(null);
        } catch (err) {
            handleErrorResponse(err);
        } finally {
            setTurnRequestLoading(false);
        }
    }

    const myHistory = myCatalogueUserId > 0 ? (basketHistoryByUserId[myCatalogueUserId] ?? []) : [];
    const currentRoundHistory = myHistory
        .filter((entry) => entry.roundNumber === roundNumber)
        .slice(-1)[0];
    const selectedItemIdsForRound = hasPendingSelection ? pendingTurnItemIds : (currentRoundHistory?.itemIds ?? []);
    const selectedItemsForRound = selectedItemIdsForRound.map((id) => {
        const row = catalogueItemsById.get(id);
        return {
            id,
            lotNo: row?.lotNo ?? id,
            forestCode: row?.forestCode ?? "—",
            product: row?.product ?? "Unknown",
            volume: Number(row?.totalVolume ?? 0),
        };
    });

    // A plain customer who is not on this catalogue must not see the auction or its items,
    // and their presence is already ignored server-side (only registered bidders count).
    const registrationResolved = !loading.users && annualState != null;
    const showNotRegistered = registrationResolved && !isRegistered && accountService.isCustomer;
    const proxyNeedsSelection = canActOnBehalf && !(onBehalfId > 0);

    if (showNotRegistered) {
        return (
            <Grid2 size={{ xs: 12 }}>
                <Typography textAlign={"center"} color="text.secondary" sx={{ mb: 1 }}>
                    Annual auction
                </Typography>
                <CatalogueItemStatusCard actualAuctionStatusId={ActualAuctionStatusEnum.NotRegistered} isAnnual />
            </Grid2>
        );
    }

    return (
        <>
            {isLastRound && !hasNoUpcomingAllowance ? (
                <Grid2 size={{ xs: 12 }}>
                    <Typography textAlign={"center"} color="warning.main" variant="body2" sx={{ mb: 1, fontWeight: 700 }}>
                        It is the last round ({annualRoundLimit}). Extra allowance +{lastRoundExtraAllowance} is applied.
                    </Typography>
                </Grid2>
            ) : null}

            {proxyNeedsSelection ? (
                <Grid2 size={{ xs: 12 }}>
                    <Alert elevation={1} severity="info">
                        Select the customer you are bidding on behalf of from the <strong>On behalf of</strong> picker in the toolbar to view their turn and place selections.
                    </Alert>
                </Grid2>
            ) : (
            <>
            <Grid2 size={{ xs: 12, md: 4 }}>
                <AuctionAnnualCataloguePanel
                    loading={loading.catalogue}
                    hasActiveCatalogue={Boolean(activeCatalogue)}
                    catalogueItems={catalogueItems}
                    pendingTurnItemIds={pendingTurnItemIds}
                    committedItemIds={committedItemIds}
                    occupiedItemIds={occupiedItemIds}
                    isItemDisabled={() => !canClientAct}
                    isItemTooLarge={(item, itemId) => !pendingTurnItemIds.includes(itemId) && Number(item.totalVolume ?? 0) > currentUserRemainingVolume}
                    isItemBauLimited={(item, itemId) => {
                        if (pendingTurnItemIds.includes(itemId)) return false;
                        const code = normalizeBauCode(item.forestCode);
                        const limit = myBauLimitMap[code];
                        if (!code || !(limit > 0)) return false;
                        const itemVolume = Number(item.totalVolume ?? 0);
                        if (!(itemVolume > 0)) return false;
                        return (myBauLeftMap[code] ?? limit) < itemVolume;
                    }}
                    getItemBauRemaining={(item) => {
                        const code = normalizeBauCode(item.forestCode);
                        const limit = myBauLimitMap[code];
                        if (!code || !(limit > 0)) return null;
                        return myBauLeftMap[code] ?? limit;
                    }}
                    onToggleItem={togglePendingItem}
                />
            </Grid2>

            <Grid2 size={{ xs: 12, md: 8 }}>
                <Grid2 container spacing={1}>
                    <Grid2 size={{ xs: 12 }}>
                        <CatalogueItemOnlineCard presence={presence} />
                    </Grid2>

                    <Grid2 size={{ xs: 12 }}>
                        <CatalogueItemStatusCard
                            actualAuctionStatusId={effectiveAnnualStatus}
                            activeMessage={<>Catalogue Item Auction is <strong>Active</strong></>}
                            isAnnual
                        />
                    </Grid2>

                    <Grid2 size={{ xs: 12 }}>
                        <AuctionAnnualCountdown timerAnchors={annualState} />
                    </Grid2>

                    {isTurnAlarmPlaying ? (
                        <Grid2 size={{ xs: 12 }}>
                            <Alert severity="warning">
                                <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 700, flex: 1 }}>
                                        It is your turn — timer is running.
                                    </Typography>
                                    <StandardButton icon="clear" variant="warning" type="button" onClick={() => stopTurnAlarm()}>
                                        Stop
                                    </StandardButton>
                                </Box>
                            </Alert>
                        </Grid2>
                    ) : null}

                    <Grid2 size={{ xs: 12 }}>
                        <AuctionAnnualCustomerTurnOrder
                            loading={loading.users}
                            isRegistered={isRegistered}
                            roundNumber={roundNumber}
                            canClientAct={canClientAct}
                            positionsUntilTurn={annualState?.myPositionsUntilTurn ?? null}
                            hasPendingSelection={hasPendingSelection}
                            turnRequestLoading={turnRequestLoading}
                            allowance={displayAvailableVolume}
                            selectedVolume={displaySelectedVolume}
                            remainingVolume={displayRemainingVolume}
                            bauLeftEntries={myBauLeftEntries}
                            pendingItemIds={pendingTurnItemIds}
                            selectedItemsForRound={selectedItemsForRound}
                            onRequestTurnAction={(action) => setTurnActionConfirm(action)}
                            onRemovePendingItem={togglePendingItem}
                        />
                    </Grid2>

                    <Grid2 size={{ xs: 12 }}>
                        <AuctionAnnualCustomerBasketPanel
                            loading={loading.users}
                            history={myHistory}
                            catalogueItemsById={catalogueItemsById}
                        />
                    </Grid2>

                    {!!params.auction?.id && !!activeCatalogueId ? (
                        <Grid2 size={{ xs: 12 }}>
                            <AuctionMessages catalogueId={activeCatalogueId} />
                        </Grid2>
                    ) : null}
                </Grid2>
            </Grid2>
            </>
            )}

            <Dialog
                open={turnActionConfirm != null}
                onClose={() => setTurnActionConfirm(null)}
                fullWidth
                maxWidth="xs"
            >
                <DialogTitle>{turnActionConfirm === "accept" ? "Accept Selection" : "Skip Turn"}</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" color="text.secondary">
                        Are you sure you want to {turnActionConfirm === "accept" ? "accept selection" : "skip turn"}?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <StandardButton icon="clear" variant="info" onClick={() => setTurnActionConfirm(null)}>
                        Cancel
                    </StandardButton>
                    <StandardButton
                        icon="check"
                        variant={turnActionConfirm === "accept" ? "success" : "primary"}
                        loading={turnRequestLoading}
                        disabled={!turnActionConfirm || turnRequestLoading}
                        onClick={() => completeTurn(turnActionConfirm !== "accept")}
                    >
                        Confirm
                    </StandardButton>
                </DialogActions>
            </Dialog>
        </>
    );
}
